import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LayoutDashboard, ShoppingCart, CreditCard, Truck, Settings, LogOut, Menu, X, Users, Handshake, Archive, UserCheck, Package, Globe, Ruler, HeadphonesIcon, Palette, MessageSquare, Mail, FileText, DollarSign, Paintbrush, Receipt, FolderOpen, Crown } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";

const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const [pendingDesignsCount, setPendingDesignsCount] = useState(0);
  const [pendingValidationCount, setPendingValidationCount] = useState(0);
  const [unpaidInvoicesCount, setUnpaidInvoicesCount] = useState(0);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'commercial') {
      const fetchUnread = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/support/tickets/unread-count`, { withCredentials: true });
          setUnreadSupportCount(res.data.count || 0);
        } catch (e) { /* ignore */ }
      };
      fetchUnread();
      const interval = setInterval(fetchUnread, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'partenaire' && user?.partner_type === 'designer') {
      const fetchPending = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/designs`, { withCredentials: true });
          const count = (res.data || []).filter(d => ['en attente', 'revision demandée'].includes(d.status)).length;
          setPendingDesignsCount(count);
        } catch (e) { /* ignore */ }
      };
      fetchPending();
      const interval = setInterval(fetchPending, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role, user?.partner_type]);

  useEffect(() => {
    if (user?.role === 'commercial') {
      const fetchPendingValidation = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/designs`, { withCredentials: true });
          const count = (res.data || []).filter(d => d.status === 'design soumis').length;
          setPendingValidationCount(count);
        } catch (e) { /* ignore */ }
      };
      fetchPendingValidation();
      const interval = setInterval(fetchPendingValidation, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'admin' || (user?.role === 'partenaire' && user?.partner_type === 'manager')) {
      const fetchUnpaid = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport/invoices/unpaid-count`, { withCredentials: true });
          setUnpaidInvoicesCount(res.data.count || 0);
        } catch (e) { /* ignore */ }
      };
      fetchUnpaid();
      const interval = setInterval(fetchUnpaid, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role, user?.partner_type]);

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/language`,
        { language: lang },
        { withCredentials: true }
      );
    } catch (e) {
      console.error('Error saving language:', e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  let navigation = [];

  if (user?.role === 'partenaire' && user?.partner_type === 'designer') {
    // Designer has its own sidebar - no Commandes
    navigation = [
      { name: t('nav_dashboard'), href: '/designer-dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
      { name: t('designs_todo'), href: '/designer-designs', icon: Paintbrush, testId: 'nav-designer-designs', badgeCount: pendingDesignsCount },
      { name: 'Patterns', href: '/designer-patterns', icon: FolderOpen, testId: 'nav-designer-patterns' },
      { name: t('invoice_recap'), href: '/designer-invoices-view', icon: Receipt, testId: 'nav-designer-invoices' },
    ];
  } else if (user?.role === 'partenaire' && user?.partner_type === 'checkin') {
    navigation = [
      { name: t('checkin_title'), href: '/checkin-dashboard', icon: Package, testId: 'nav-checkin' },
    ];
  } else if (user?.role === 'partenaire' && user?.partner_type === 'transport') {
    navigation = [
      { name: t('nav_dashboard'), href: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
      { name: t('transport_orders_nav'), href: '/transport-orders', icon: Truck, testId: 'nav-transport-orders' },
    ];
  } else {
    navigation = [
      { name: t('nav_dashboard'), href: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
      { name: t('nav_orders'), href: '/orders', icon: ShoppingCart, testId: 'nav-orders' },
    ];
  }

  if (user?.role === 'admin') {
    navigation.push(
      { name: t('nav_clients'), href: '/clients', icon: UserCheck, testId: 'nav-clients' },
      { name: t('nav_products'), href: '/products', icon: Package, testId: 'nav-products' },
      { name: t('nav_archives'), href: '/archives', icon: Archive, testId: 'nav-archives' },
      { name: t('nav_commercials'), href: '/commercials', icon: Users, testId: 'nav-commercials' },
      { name: t('nav_partners'), href: '/partners', icon: Handshake, testId: 'nav-partners' },
      { name: t('nav_expenses'), href: '/expenses', icon: CreditCard, testId: 'nav-expenses' },
      { name: t('nav_transport'), href: '/transport', icon: Truck, testId: 'nav-transport' },
      { name: 'Transport Suivi', href: '/transport-suivi', icon: Truck, testId: 'nav-transport-suivi' },
      { name: t('product_pricing_title'), href: '/product-pricing', icon: DollarSign, testId: 'nav-product-pricing' },
      { name: t('client_quotes_nav'), href: '/client-quotes', icon: FileText, testId: 'nav-client-quotes' },
      { name: t('designs_nav'), href: '/design-requests', icon: Paintbrush, testId: 'nav-designs-admin' },
      { name: 'Templates Maillots', href: '/design-admin', icon: Palette, testId: 'nav-design-admin' },
      { name: 'Premium', href: '/premium-admin', icon: Crown, testId: 'nav-premium-admin' },
      { name: t('invoice_recap_nav'), href: '/designer-invoices', icon: Receipt, testId: 'nav-designer-invoices' },
      { name: t('nav_settings'), href: '/settings', icon: Settings, testId: 'nav-settings' },
      { name: t('support'), href: '/support', icon: MessageSquare, testId: 'nav-support' }
    );
  }

  if (user?.role === 'commercial') {
    navigation.push(
      { name: t('quotes_nav'), href: '/quotes', icon: FileText, testId: 'nav-quotes' },
      { name: t('design_requests_nav'), href: '/design-requests', icon: Paintbrush, testId: 'nav-design-requests', badgeCount: pendingValidationCount },
      { name: 'Génération IA', href: '/design-generator', icon: Palette, testId: 'nav-design-generator' },
      { name: t('size_guide'), href: '/size-guide', icon: Ruler, testId: 'nav-size-guide' },
      { name: t('email_templates_nav'), href: '/email-templates', icon: Mail, testId: 'nav-email-templates' },
      { name: t('support'), href: '/support', icon: MessageSquare, testId: 'nav-support' },
      { name: t('personalization'), href: '/personalization', icon: Palette, testId: 'nav-personalization' }
    );
  }

  if (user?.role === 'partenaire' && user?.partner_type === 'manager') {
    navigation.push(
      { name: 'Transport Suivi', href: '/transport-suivi', icon: Truck, testId: 'nav-transport-suivi' }
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 p-4">
        <div className="flex items-center justify-between">
          <img src={LOGO_URL} alt="Panther" className="h-8 object-contain" data-testid="mobile-logo" />
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="lang-switcher-mobile" className="w-24 h-8 text-xs border-slate-200">
                <Globe size={14} className="mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">FR</SelectItem>
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="vi">VI</SelectItem>
              </SelectContent>
            </Select>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-slate-600 hover:text-slate-900"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <div className="flex pt-16 md:pt-0">
        <aside
          className={`fixed md:static inset-y-0 left-0 transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 transition-transform duration-200 ease-in-out z-40 w-64 bg-white border-r border-slate-200 flex flex-col`}
        >
          <div className="p-6 border-b border-slate-200">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="sidebar-logo" />
            <p className="text-xs text-slate-600 mt-2">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const showBadge = item.href === '/support' && (user?.role === 'admin' || user?.role === 'commercial') && unreadSupportCount > 0;
              const showItemBadge = item.badgeCount > 0;
              const showTransportBadge = item.href === '/transport-suivi' && unpaidInvoicesCount > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={item.testId}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-slate-100 text-slate-900 font-medium'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <item.icon size={18} />
                  {item.name}
                  {showItemBadge && (
                    <span data-testid="pending-designs-badge" className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                      {item.badgeCount}
                    </span>
                  )}
                  {showBadge && (
                    <span data-testid="support-unread-badge" className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                      {unreadSupportCount}
                    </span>
                  )}
                  {showTransportBadge && (
                    <span data-testid="transport-unpaid-badge" className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                      {unpaidInvoicesCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-slate-500" />
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger data-testid="lang-switcher-sidebar" className="h-8 text-xs border-slate-200 flex-1">
                  <SelectValue>{LANG_LABELS[language]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="vi">Tiếng Việt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            >
              <LogOut size={18} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
